import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Package, MapPin, Phone, ShoppingCart, CheckCircle2, Clock, Truck, Wallet, Smartphone, Store, ShoppingBag } from "lucide-react";
import { grocery, formatXAF } from "../../lib/grocery";

const DELIVERY_FLOW = [
  { key: "pending", label: "En attente", icon: Clock },
  { key: "confirmed", label: "Confirmée", icon: CheckCircle2 },
  { key: "in_delivery", label: "En livraison", icon: Truck },
  { key: "delivered", label: "Livrée", icon: Package },
];

const PICKUP_FLOW = [
  { key: "pending", label: "En attente", icon: Clock },
  { key: "confirmed", label: "Confirmée", icon: CheckCircle2 },
  { key: "ready_for_pickup", label: "Prête pour retrait", icon: ShoppingBag },
  { key: "picked_up", label: "Retirée", icon: Package },
];

export default function OrderTrackPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    const fetchOrder = async () => {
      try {
        const o = await grocery.order(id);
        if (alive) setOrder(o);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Commande introuvable");
      }
    };
    fetchOrder();
    const t = setInterval(fetchOrder, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [id]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-10">
        <Link to="/courses" className="text-brand-inkSoft text-sm inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" /> Retour</Link>
        <Card className="p-6 rounded-2xl border-brand-line"><p className="text-brand-terracotta">{err}</p></Card>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-10" data-testid="grocery-order-loading">
        <p className="text-brand-inkSoft">Chargement…</p>
      </div>
    );
  }

  const isPickup = order.fulfillment_type === "pickup";
  const flow = isPickup ? PICKUP_FLOW : DELIVERY_FLOW;
  const statusIdx = flow.findIndex((s) => s.key === order.status);
  const payIcon = order.payment_method === "cash" ? <Wallet className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />;
  const payLabel =
    order.payment_method === "cash"
      ? (isPickup ? "Espèces au retrait" : "Espèces à la livraison")
      : order.payment_method === "airtel" ? "Airtel Money" : "Moov Money";
  const paidBadge = order.paid
    ? "Payée"
    : (isPickup ? "À régler au retrait" : "À régler à la livraison");

  return (
    <div className="min-h-[80vh] bg-brand-paper pb-24" data-testid="grocery-order-page">
      <section className="bg-brand-ink text-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-6">
          <Link to="/courses" className="text-white/70 text-sm flex items-center gap-1 mb-2 hover:text-brand-gold" data-testid="grocery-order-back">
            <ArrowLeft className="w-4 h-4" /> Boutique
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold inline-flex items-center gap-2">
            <ShoppingCart className="w-6 h-6" /> Commande #{order.id.slice(-6).toUpperCase()}
          </h1>
          <div className="text-sm text-white/80 mt-1 inline-flex flex-wrap items-center gap-2" data-testid="grocery-order-fulfillment-badge">
            <Badge className={isPickup ? "bg-brand-gold text-brand-black hover:bg-brand-goldDark" : "bg-brand-terracotta hover:bg-brand-terracottaHover"}>
              {isPickup ? <><Store className="w-3 h-3 mr-1" /> Retrait</> : <><Truck className="w-3 h-3 mr-1" /> Livraison</>}
            </Badge>
            <Badge className={order.paid ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-500 hover:bg-amber-600"}>
              {paidBadge}
            </Badge>
            <span className="inline-flex items-center gap-1">{payIcon} {payLabel}</span>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-4">
        {/* Status timeline */}
        <Card className="p-5 rounded-2xl border-brand-line" data-testid="grocery-order-status">
          <h3 className="font-heading font-semibold text-brand-ink mb-4">
            Suivi {isPickup ? "(retrait en magasin)" : "(livraison)"}
          </h3>
          <div className="grid grid-cols-4 gap-1 relative">
            {flow.map((s, i) => {
              const done = i <= statusIdx;
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex flex-col items-center text-center" data-testid={`grocery-order-step-${s.key}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-1 ${
                    done ? "bg-brand-gold text-brand-black" : "bg-brand-sandlight text-brand-inkMuted"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider ${done ? "text-brand-ink font-semibold" : "text-brand-inkMuted"}`}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Pickup addresses (multi-shop) */}
        {isPickup && (
          <Card className="p-5 rounded-2xl border-brand-line" data-testid="grocery-order-pickup-info">
            <h3 className="font-heading font-semibold text-brand-ink mb-3 inline-flex items-center gap-2">
              <Store className="w-5 h-5 text-brand-gold" />
              Point{(order.pickup_addresses || []).length > 1 ? "s" : ""} de retrait
              ({(order.pickup_addresses || []).length})
            </h3>
            {(order.pickup_addresses || []).length > 1 && (
              <p className="text-xs text-brand-inkSoft mb-3 bg-amber-50 border border-amber-200 rounded-lg p-2">
                Cette commande contient des produits de plusieurs boutiques. Vous devrez passer dans chacune d'elles.
              </p>
            )}
            <div className="space-y-2">
              {(order.pickup_addresses || []).map((s, i) => (
                <div key={s.shop_id} className="flex items-start gap-3 p-3 rounded-xl bg-brand-sandlight" data-testid={`grocery-order-pickup-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-white text-brand-gold flex items-center justify-center shrink-0 font-bold">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-brand-ink">{s.name}</div>
                    <div className="text-xs text-brand-inkSoft inline-flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />{s.address || "Adresse à confirmer"}
                    </div>
                    {s.phone && (
                      <a href={`tel:${s.phone}`} className="text-xs text-brand-goldDark hover:underline inline-flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3" />{s.phone}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-brand-inkSoft mt-3">
              Présentez votre numéro de commande <strong>#{order.id.slice(-6).toUpperCase()}</strong> au comptoir.
            </p>
          </Card>
        )}

        {/* Delivery address */}
        {!isPickup && (
          <Card className="p-5 rounded-2xl border-brand-line">
            <h3 className="font-heading font-semibold text-brand-ink mb-2 inline-flex items-center gap-2">
              <Truck className="w-5 h-5 text-brand-gold" /> Adresse de livraison
            </h3>
            <p className="text-sm text-brand-ink"><MapPin className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-brand-gold" /> {order.delivery_address}</p>
            <p className="text-xs text-brand-inkSoft mt-1">Zone : {order.delivery_zone}</p>
            <p className="text-xs text-brand-inkSoft"><Phone className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> {order.customer_phone}</p>
            {order.express && <Badge className="mt-2 bg-brand-terracotta hover:bg-brand-terracottaHover">⚡ Express</Badge>}
            {order.notes && <p className="text-xs text-brand-inkSoft mt-2 italic">"{order.notes}"</p>}
          </Card>
        )}

        {/* Customer contact (shown for pickup too) */}
        {isPickup && (
          <Card className="p-5 rounded-2xl border-brand-line">
            <h3 className="font-heading font-semibold text-brand-ink mb-2">Contact</h3>
            <p className="text-xs text-brand-inkSoft"><Phone className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> {order.customer_phone}</p>
            {order.notes && <p className="text-xs text-brand-inkSoft mt-2 italic">"{order.notes}"</p>}
          </Card>
        )}

        {/* Items */}
        <Card className="p-5 rounded-2xl border-brand-line">
          <h3 className="font-heading font-semibold text-brand-ink mb-3">Articles</h3>
          <div className="divide-y divide-brand-line">
            {(order.items || []).map((it) => (
              <div key={it.product_id} className="py-2.5 flex justify-between text-sm" data-testid={`grocery-order-line-${it.product_id}`}>
                <div>
                  <div className="font-semibold text-brand-ink">{it.name}</div>
                  <div className="text-xs text-brand-inkSoft">
                    {it.quantity} × {formatXAF(it.unit_price_xaf)} / {it.unit}
                    {it.shop_name ? ` · ${it.shop_name}` : ""}
                  </div>
                </div>
                <div className="font-semibold text-brand-ink">{formatXAF(it.line_total_xaf)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-brand-inkSoft">Sous-total</span><span>{formatXAF(order.subtotal_xaf)}</span></div>
            <div className="flex justify-between">
              <span className="text-brand-inkSoft">{isPickup ? "Frais de retrait" : "Livraison"}</span>
              <span>{formatXAF(order.delivery_fee_xaf)}</span>
            </div>
            <div className="flex justify-between items-baseline pt-2 border-t border-brand-line mt-2">
              <span className="font-semibold text-brand-ink">Total</span>
              <span className="font-heading text-2xl font-bold text-brand-goldDark" data-testid="grocery-order-total">
                {formatXAF(order.total_xaf)}
              </span>
            </div>
          </div>
        </Card>

        <Link to="/courses">
          <Button className="w-full h-12 rounded-xl bg-brand-ink hover:bg-brand-inkSoft text-white" data-testid="grocery-order-continue">
            Continuer mes courses
          </Button>
        </Link>
      </section>
    </div>
  );
}
